import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { BlogPost } from '../data/blogPosts';
import { format } from 'date-fns';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

export default function BlogCard({ post, featured = false }: BlogCardProps) {
  const formattedDate = format(new Date(post.publishedAt), 'MMM d, yyyy');

  if (featured) {
    return (
      <a
        href={`/blog/${post.slug}`}
        className="group block bg-gradient-to-br from-[#1A535C] to-[#14424a] rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300"
      >
        <div className="p-8 md:p-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-white/10 text-[#4ECDC4] rounded-full text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-[#4ECDC4] transition-colors">
            {post.title}
          </h2>

          <p className="text-white/70 text-lg mb-6 line-clamp-3">
            {post.description}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white/60 text-sm">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[#4ECDC4] font-medium group-hover:gap-3 transition-all">
              <span>Read article</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={`/blog/${post.slug}`}
      className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-[#4ECDC4] transition-all duration-300"
    >
      <div className="p-6">
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-0.5 bg-[#4ECDC4]/10 text-[#1A535C] rounded-full text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1A535C] transition-colors line-clamp-2">
          {post.title}
        </h3>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {post.description}
        </p>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{post.readingTime} min</span>
            </div>
          </div>

          <div className="text-[#FF6B6B] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            <span>Read</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </a>
  );
}
